import { ParamType, ParamTypeName, paramTypeNameSchema, paramTypeSchema } from "@bonusmap/api-types";
import z from "zod";
/**
 * tipi per i parametri di BonusAddAI
 */
// =========================================================
// tipi per i parametri di output
// =========================================================
// schema zod per OutputParamItem
export const OutputParamItemSchema = z.object({
    parameter_id: z.number().int().positive(),
    value: z.any()
});
export const OutputParamGroupSchema = z.array(OutputParamItemSchema);
export const OutputParamGroupsSchema = z.array(OutputParamGroupSchema);
export type OutputParamItem  = z.infer<typeof OutputParamItemSchema>;
export type OutputParamGroup = z.infer<typeof OutputParamGroupSchema>;
export type OutputParamGroups = z.infer<typeof OutputParamGroupsSchema>;

// =========================================================
// tipi per i parametri di input
// =========================================================
// schema zod per InputParamItem
export const InputParamItemSchema = z.object({
    parameter_id: z.number().int().positive().optional(), // finché non associato può essere assente
    value: z.any(), // valore proposto dall'AI
    type: paramTypeNameSchema.optional(), // tipo di parametro proposto dall'AI (stringa)
    typeValue: z.string().optional(), // tipo di valore proposto dall'AI
    name: z.string().optional(), // nome del parametro
    summary: z.string().optional(), // breve descrizione del parametro
    similars: z.array(z.lazy(() => InputParamSimilarItemSchema)).optional() // parametri simili trovati nel sistema
});

// schema zod per group di InputParamItem
export const InputParamGroupSchema = z.array(InputParamItemSchema);

// schema zod per array di gruppi di InputParamItem (struttura logica OR/AND)
export const InputParamGroupsSchema = z.array(InputParamGroupSchema);

// Parametro simile trovato nella lista dei parametri del sistema
export const InputParamSimilarItemSchema = z.object({
    id: z.number().int().positive(),
    name: z.string(),
    description: z.string().optional(),
    type_name: paramTypeNameSchema,
    type_value: z.string(),
    attribute_id: z.number().int().positive().nullable(),
    attribute_name: z.string().optional(),
    ranking: z.number().min(0) // punteggio di similarità
});

export type InputParamSimilarItem = z.infer<typeof InputParamSimilarItemSchema>;
export type InputParamItem  = z.infer<typeof InputParamItemSchema>;
export type InputParamGroup = z.infer<typeof InputParamGroupSchema>;
export type InputParamGroups = z.infer<typeof InputParamGroupsSchema>;

export const  isValidTypeName = (value: any): value is ParamTypeName => {
    return typeof value === 'string' && paramTypeNameSchema.safeParse(value).success;
}
export  const isValidParamType = (value: any): value is ParamType => {
    return typeof value === 'string' && paramTypeSchema.safeParse(value).success;
}
export const isValidInputParamItem = (value: any): value is InputParamItem => {
    return InputParamItemSchema.safeParse(value).success;
}
export const isValidOutputParamItem = (value: any): value is OutputParamItem => {
    return OutputParamItemSchema.safeParse(value).success;
}
export const  isValidInputParamGroup = (value: any): value is InputParamGroup => {
    return InputParamGroupSchema.safeParse(value).success;
}
export const isValidInputParamGroups = (value: any): value is InputParamGroups => {
    return InputParamGroupsSchema.safeParse(value).success;
}
export const convertToOutputParamItem = (input: InputParamItem): OutputParamItem | null => {
    if (isValidInputParamItem(input) && input.parameter_id) {
        return {
            parameter_id: input.parameter_id,
            value: input.value
        };
    }
    return null;
}
export const convertToOutputParamGroup = (inputGroup: InputParamGroup): OutputParamGroup => {
    const outputGroup: OutputParamGroup = [];
    inputGroup.forEach(inputItem => {
        const outputItem = convertToOutputParamItem(inputItem);
        if (outputItem) {
            outputGroup.push(outputItem);
        }
    });
    return outputGroup;
}
export const convertToOutputParamGroups = (inputGroups: InputParamGroups): OutputParamGroups => {
    const outputGroups: OutputParamGroups = [];
    inputGroups.forEach(inputGroup => {
        const outputGroup = convertToOutputParamGroup(inputGroup);
        if (outputGroup.length > 0) {
            outputGroups.push(outputGroup);
        }
    });
    return outputGroups;
}